import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ModuloPermissaoDashboard, PERMISSAO_TOTAL, SEM_PERMISSAO } from '../../models/dashboard/modulo-permissao.model';
import { PermissaoDashboardService } from '../dashboard/permissao-dashboard.service';

/**
 * Service global (singleton) que gerencia o estado de permissoes do usuario.
 * Carrega permissoes uma unica vez apos autenticacao e expoe via BehaviorSubject.
 */
@Injectable({ providedIn: 'root' })
export class PermissaoService {
  private permissoes$ = new BehaviorSubject<ModuloPermissaoDashboard>(SEM_PERMISSAO);
  private carregado$ = new BehaviorSubject<boolean>(false);

  constructor(
    private permissaoDashboardService: PermissaoDashboardService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Carrega permissoes do usuario. Chamado apos autenticacao.
   * Admin recebe PERMISSAO_TOTAL sem requisicao HTTP.
   */
  carregarPermissoes(email: string): Observable<void> {
    // Verificar se usuario e admin via storage
    if (isPlatformBrowser(this.platformId)) {
      const categoriaAdmin = sessionStorage.getItem('categoriaAdmin') || localStorage.getItem('categoriaAdmin');
      if (categoriaAdmin === 'true') {
        this.permissoes$.next({ ...PERMISSAO_TOTAL });
        this.carregado$.next(true);
        return of(undefined);
      }
    }

    // Nao-admin: buscar permissoes do backend
    return this.permissaoDashboardService.buscarPermissoes(email).pipe(
      tap((permissoes) => {
        this.permissoes$.next(permissoes);
        this.carregado$.next(true);
      }),
      catchError(() => {
        // Fallback: permissao total (graceful degradation — seguranca real esta no backend)
        this.permissoes$.next({ ...PERMISSAO_TOTAL });
        this.carregado$.next(true);
        return of(undefined);
      }),
      // Converter para Observable<void>
      tap(() => {}),
    ) as unknown as Observable<void>;
  }

  /** Verifica se usuario tem permissao para um campo especifico. */
  temPermissao(campo: keyof ModuloPermissaoDashboard): boolean {
    return this.permissoes$.getValue()[campo];
  }

  /** Observable que emite quando permissoes estao carregadas. */
  aguardarCarregamento(): Observable<boolean> {
    return this.carregado$.asObservable();
  }

  /**
   * Retorna a primeira rota acessivel (cascata).
   * Usado para redirecionamento inteligente pos-login.
   */
  obterPrimeiraRotaAcessivel(): string {
    const p = this.permissoes$.getValue();
    if (p.dashboard) return '/dashboard';
    if (p.pontoDeVenda) return '/vendas';
    if (p.produto) return '/produtos';
    if (p.cliente) return '/clientes';
    if (p.fornecedor) return '/fornecedores';
    if (p.compra) return '/adicionar-compra';
    if (p.conta) return '/contas';
    if (p.gastos) return '/nova-despesa';
    if (p.receitas) return '/nova-receita';
    if (p.configuracao) return '/configuracoes-da-empresa';
    return '/sem-permissao';
  }

  /**
   * Carrega permissao total imediatamente, sem requisicao HTTP.
   * Usado quando ha sessao valida mas sem email no storage (ex: sessao de versao anterior do app).
   * O backend continua sendo a fonte real de autoridade — essa e apenas uma permissao de fallback no frontend.
   */
  carregarComPermissaoTotal(): void {
    this.permissoes$.next({ ...PERMISSAO_TOTAL });
    this.carregado$.next(true);
  }

  /** Limpa permissoes (chamado no logout). */
  limparPermissoes(): void {
    this.permissoes$.next({ ...SEM_PERMISSAO });
    this.carregado$.next(false);
  }

  /** Observable das permissoes para uso reativo. */
  getPermissoes$(): Observable<ModuloPermissaoDashboard> {
    return this.permissoes$.asObservable();
  }
}
